<template>
  <div class="d-flex flex-column gap-5">
    <div class="d-flex flex-column gap-2">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.whatAssetNeedsToBeMoved') }}
      </span>

      <yard-view-asset-select
        clearable
        include-assigned-assets
        include-associated-to-active-move-tasks="disabled"
        persistent-hint
        v-model="selectedAsset"
        :disabled="disabledFields?.asset"
        :hint="t('yard.drawers.createTaskDrawer.helpTexts.leaveItBlankForTheDriverToChoose')"
        :placeholder="t('yard.drawers.createTaskDrawer.placeholders.searchByAssetId')"
        :rules="[validateAsset]"></yard-view-asset-select>
    </div>

    <div class="d-flex flex-row gap-5">
      <div class="d-flex flex-column gap-2 flex-grow-0 flex-shrink-1" style="min-width: 100px">
        <span class="label typography-body-sm-bold">
          {{ t('yard.drawers.createTaskDrawer.labels.from') }}
        </span>

        <div class="d-flex align-center gap-1" style="min-block-size: 40px">
          <yard-spot-presenter
            v-if="selectedOriginSpot"
            data-testid="yard-move-task-details-origin-spot"
            :spot-id="selectedOriginSpot" />
          <small v-else class="d-flex align-center gap-1 my-auto">
            <v-icon size="small">mdi-map-marker-outline</v-icon>
            <span class="typography-body-sm text--color-text-placeholder">
              {{
                t('yard.drawers.createTaskDrawer.helpTexts.selectAnAssetToViewItsCurrentLocation')
              }}
            </span>
          </small>
        </div>
      </div>

      <v-icon size="small" class="my-auto">mdi-arrow-right</v-icon>

      <div class="d-flex flex-column gap-2 flex-grow-1 flex-shrink-0" style="min-width: 50%">
        <span class="label typography-body-sm-bold">
          {{ t('yard.drawers.createTaskDrawer.labels.to') }}
        </span>

        <!--
            We use include-reserved-spots="disabled" because we don't want to allow the user to choose a reserved spot
            as the destination spot. This is specially useful when we are editing a task, in which case the previously
            selected destination spot will be reserved, but we still need to show it.
        -->
        <yard-view-spot-select
          clearable
          persistent-hint
          show-spot-status
          include-reserved-spots="disabled"
          include-assigned-spots="disabled"
          include-associated-to-active-move-tasks="disabled"
          v-model="selectedDestinationSpot"
          :asset-type="assetType"
          :disabled="disabledFields?.destinationSpot"
          :hint="t('yard.drawers.createTaskDrawer.helpTexts.leaveItBlankForTheDriverToChoose')"
          :placeholder="t('yard.drawers.createTaskDrawer.placeholders.searchASpot')"
          :rules="[validateDestinationSpot]"></yard-view-spot-select>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import useYard from '@/modules/yard/composables/useYard';
import YardViewSpotSelect from '@/modules/yard/components/Selects/YardViewSpotSelect.vue';
import YardViewAssetSelect from '@/modules/yard/components/Selects/YardViewAssetSelect.vue';
import { isMoveDetailsValid } from '@/modules/yard/components/YardTasksCreateTaskMoveDetails.helpers';

import YardSpotPresenter from '@satellite/components/elements/presenters/yard/YardSpotPresenter.vue';
import { useYardAssetPresenterV2 } from '@satellite/composables/useYardAssetPresenterV2';

export default defineComponent({
  name: 'YardTasksCreateTaskMoveDetails',
  // keeping the input event just for "consistency" with existing components; I don't think it makes sense in such context though
  emits: ['input', 'change'],
  components: {
    YardViewAssetSelect,
    YardViewSpotSelect,
    YardSpotPresenter
  },
  props: {
    asset: {
      type: String,
      required: false,
      default: null
    },
    originSpot: {
      type: String,
      required: false,
      default: null
    },
    destinationSpot: {
      type: String,
      required: false,
      default: null
    },
    disabledFields: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  setup(props, { emit }) {
    const yardStore = useYard();
    const { t } = useI18n();

    const selectedAsset = ref(props.asset);
    const selectedOriginSpot = ref(props.originSpot);
    const selectedDestinationSpot = ref(props.destinationSpot);

    const { spotId: fetchedSpotId, onIntersect } = useYardAssetPresenterV2({
      assetId: selectedAsset,
      asset: null
    });

    // TODO: add the headless option to the useYardAssetPresenterV2 as soon as the feature is available in the presenter
    onMounted(() => {
      onIntersect(true);
    });

    const validateAsset = value => {
      if (isMoveDetailsValid(value, selectedDestinationSpot.value)) {
        return true;
      }

      return t('yard.drawers.createTaskDrawer.errors.youNeedToProvideEitherAnAssetOrASpot');
    };

    const validateDestinationSpot = value => {
      if (isMoveDetailsValid(selectedAsset.value, value)) {
        return true;
      }

      return t('yard.drawers.createTaskDrawer.errors.youNeedToProvideEitherAnAssetOrASpot');
    };

    const assetType = computed(() => {
      if (!selectedAsset.value) {
        return null;
      }

      // TODO: should this be in a separate composable?
      return yardStore.isAssetContainer(selectedAsset.value) ? 'assetContainer' : 'assetVisit';
    });

    watch(selectedAsset, newSelectedAsset => {
      /**
       * We clear the destination spot when the asset changes
       * because the asset type can change, making the destination spot invalid
       */
      if (newSelectedAsset) {
        selectedDestinationSpot.value = null;
      }
    });

    watch(fetchedSpotId, newFetchedSpotId => {
      selectedOriginSpot.value = newFetchedSpotId;
    });

    watch(
      [selectedAsset, selectedOriginSpot, selectedDestinationSpot],
      ([newSelectedAsset, newSelectedOriginSpot, newSelectedDestinationSpot]) => {
        const eventData = {
          asset: newSelectedAsset,
          originSpot: newSelectedOriginSpot,
          destinationSpot: newSelectedDestinationSpot
        };

        emit('input', eventData);
        emit('change', eventData);
      }
    );

    return {
      t,
      selectedAsset,
      selectedOriginSpot,
      selectedDestinationSpot,
      validateAsset,
      validateDestinationSpot,
      assetType
    };
  }
});
</script>
